import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import { ODataParams, odataParamsFactory } from '@tdw/core';
import { FlatterFilesResponse } from 'src/app/models';
import { ConfigurationFilter, ProductConfiguration, FittingDetail, FittingQueryParams, Inventtxt } from '../../models/fitting-query-params.model';
import { AppLookup, ConfigurationFiltersLookups, FittingQueryResult, LookupValue, lookupValueFactory } from '../../models/lookup.model';

import * as fromActions from '../actions/fittings.actions';
import * as fromSearch from '../fromSearch.barrel';

export const fittingsFeatureKey = 'fittings';


export enum Loaders {
  loadingResult = 'Loading Result...',
  loadingLookups = 'Loading Lookup Values...',
  loadingDetail = 'Loading Details...',
  loadingConfigurations = 'Loading Configurations...',
  loadingDrawing = 'Loading Drawing...',
  loadingAdditionalDrawings = "loadingAdditionalDrawings",
  loadConfigurationLookupOptions = "loadConfigurationLookupOptions"
}

export interface State {
  fittingTypes: (LookupValue & { imageUrl?: string })[];
  pipelineTypes: LookupValue[];
  regions: LookupValue[];
  pipeSizes: LookupValue[];
  ansiClasses: LookupValue[];
  flangeTypes: LookupValue[];
  designCodes: LookupValue[];
  designFactors: LookupValue[];
  maxCes: LookupValue[];
  pipelineType?: number;
  query?: FittingQueryParams;
  filterResult: FittingQueryResult[];
  activeFittingId?: number | null;
  activeFittingPartnumber?: string | null; // this has to be done to accomodate the ability to select a configuration
  activeFittingDetail?: FittingDetail;
  activeFittingConfigurations?: ProductConfiguration[];
  activeFittingCommercialDescriptions?: Inventtxt[];
  activeFittingDrawings: { [key: string]: FlatterFilesResponse };
  lowTempFkmOringsCommonIncompatibleProducts: string[];
  configurationsFilter?: ConfigurationFilter;
  configurationFilterAssociatedParts?: ConfigurationFilter;
  activeFittingSelectedAssociatedPart?: string; // this lets us filter the configurations from the store for a given associated part
  configurationFiltersLookup?: ConfigurationFiltersLookups;
  loaders: Loaders[]
}



export const initialState: State = {
  activeFittingDrawings: {},
  pipelineTypes: [
    lookupValueFactory({ id: 0, text: 'Standard (ANSI)', sortOrder: 1 }),
    lookupValueFactory({ id: 1, text: 'Special (ISO, GOST,...)', sortOrder: 2 })
  ],
  regions: [],
  pipeSizes: [],
  ansiClasses: [],
  flangeTypes: [],
  designCodes: [],
  designFactors: [],
  maxCes: [],
  fittingTypes: [],
  filterResult: [],
  lowTempFkmOringsCommonIncompatibleProducts: [
    'Acetic Acid (any form or compound)',
    'Ethanol',
    'Acetone',
    'Hydrogen sulfide (any form or compound)',
    'Amine mixtures',
    'Methanol (any form or compound)',
    'Ammonia (any form or compount)',
    'Refrigerants of any kind',
    'Ammonium (any form or compound)',
    'Steam'
  ],
  query: { classId: -1, limit125: -1, isDirty: false },
  pipelineType: 0,
  configurationFiltersLookup: {
    bevelOptions: [],
    oringOptions: [],
    tappedHoleSizes: []
  },
  loaders: []
};



export const reducer = createReducer(
  initialState,
  on(fromActions.loadFittingLookups, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingLookups)
  })),
  on(fromActions.loadFittingLookupsSuccess, (state, action) => ({
    ...state,
    pipeSizes: action.data.pipeSizes,
    ansiClasses: action.data.ansiClasses,
    flangeTypes: action.data.flangeTypes,
    designCodes: action.data.designCodes,
    maxCes: action.data.maxCes,
    designFactors: action.data.designFactors,
    regions: action.data.regions,
    fittingTypes: action.data.fittingTypes,
    loaders: state.loaders.filter(f => f !== Loaders.loadingLookups)
  })),
  on(fromActions.loadFittingProducts, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingResult)
  })),
  on(fromActions.loadFittingProductsSuccess, (state, action) => {
    return {
      ...state,
      filterResult: action.res,
      loaders: state.loaders.filter(f => f !== Loaders.loadingResult)
    };
  }),
  on(fromActions.setActiveFitting, (state, action) => ({
    ...state,
    activeFittingId: action.id,
    activeFittingPartnumber: action.partNumber,
    loaders: state.loaders.concat(Loaders.loadingDetail).concat(Loaders.loadingConfigurations)
  })),
  on(fromActions.loadActiveFittingConfigurationsSuccess, (state, action) => ({
    ...state,
    activeFittingConfigurations: action.configurations,
    loaders: state.loaders.filter(f => f !== Loaders.loadingConfigurations)
  })),
  on(fromActions.clearActiveFitting, (state, action) => ({
    ...state,
    activeFittingId: null,
    activeFittingPartnumber: null,
    activeFittingDetail: undefined,
    activeFittingConfigurations: [],
    activeFittingCommercialDescriptions: [],
    lowTempFkmOringsCommonIncompatibleProducts: []
  })),
  on(fromActions.changeRegion, (state, action) => ({
    ...state,
    query: {
      ...state.query,
      hemisphereId: action.value,
      isDirty: true
    },
  })),
  on(fromActions.changePipelineType, (state, action) => ({
    ...state,
    pipelineType: action.value,
  })),
  on(fromActions.changeSize, (state, action) => ({
    ...state,
    query: {
      ...state.query,
      pipeSizeId: action.value,
      isDirty: true
    },
  })),
  on(fromActions.changeFittingType, (state, action) => ({
    ...state,
    query: {
      ...state.query,
      fittingTypeId: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeAnsiClass, (state, action) => ({
    ...state,

    query: {
      ...state.query,
      classId: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeFlangeType, (state, action) => ({
    ...state,

    query: {
      ...state.query,
      flangeTypeId: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeBranchSize, (state, action) => ({
    ...state,

    query: {
      ...state.query,
      branchSizeId: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeDesignCode, (state, action) => {

    return {
      ...state,
      query: {
        ...state.query,
        designCodeId: action.value,
        isDirty: true

      }
    }
  }),
  on(fromActions.changeDesignFactor, (state, action) => ({
    ...state,
    query: {
      ...state.query,
      designFactorId: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeMaxDesignPressure, (state, action) => ({
    ...state,
    query: {
      ...state.query,
      maxDesignPressure: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeDesignTempMax, (state, action) => ({
    ...state,
    query: {
      ...state.query,
      designTempMax: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeDesignTempMin, (state, action) => ({
    ...state,

    query: {
      ...state.query,
      designTempMin: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeMaxCe, (state, action) => ({
    ...state,

    query: {
      ...state.query,
      maxCeId: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeLimit125, (state, action) => ({
    ...state,
    query: {
      ...state.query,
      limit125: action.value,
      isDirty: true
    }
  })),
  on(fromActions.changeIsBuyAmerica, (state, action) => ({
    ...state,
    query: {
      ...state.query,
      isBuyAmerica: action.value,
      isDirty: true
    }
  })),
  on(fromActions.resetFilter, (state, action) => ({
    ...state,
    query: { ...initialState.query, isDirty: false }
  })),
  on(fromActions.changePipelineType, (state, action) => ({
    ...state,
    pipelineType: action.value,
  })),
  on(fromActions.loadActiveFittingDetailsSuccess, (state, action) => ({
    ...state,
    activeFittingDetail: action.obj?.fittingDetail,
    activeFittingConfigurations: action.obj?.configurations,
    activeFittingCommercialDescriptions: action.obj?.commercialDescriptions,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDetail)
  })),

  on(fromActions.changeTopLevelConfigurationFilter, (state, action) => ({
    ...state,
    configurationsFilter: { ...state.configurationsFilter, ...action.obj }
  })),
  on(fromActions.changeAssociateProductConfigurationFilter, (state, action) => ({
    ...state,
    configurationFilterAssociatedParts: { ...state.configurationFilterAssociatedParts, ...action.obj }
  })),
  on(fromActions.removeLoader, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== action.loader)
  })),
  on(fromActions.loadFittingAssociatedDrawing, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingDrawing)
  })),
  on(fromActions.loadFittingAssociatedDrawingSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDrawing)
  })),
  on(fromActions.loadFittingAssociatedDrawingFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDrawing)
  })),
  on(fromActions.loadFittingDrawing, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingDrawing)
  })),
  on(fromActions.loadFittingDrawingSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDrawing)
  })),
  on(fromActions.loadFittingDrawingFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDrawing)
  })),
  on(fromActions.loadDrawingFromFlatterFiles, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingDrawing)
  })),
  on(fromActions.loadDrawingFromFlatterFilesSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDrawing)
  })),
  on(fromActions.loadDrawingFromFlatterFilesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDrawing)
  })),
  on(fromActions.loadActiveFittingDrawingDictionary, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingAdditionalDrawings)
  })),
  on(fromActions.loadActiveFittingDrawingDictionarySuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingAdditionalDrawings),
    activeFittingDrawings: action.dictionary
  })),
  on(fromActions.loadActiveFittingDrawingDictionaryFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingAdditionalDrawings)
  })),
  on(fromSearch.activateSearchItem, (state, action) => ({
    ...state,
    activeFittingId: action.item.productID,
    activeFittingPartnumber: action.item.partNumber,
    loaders: [Loaders.loadingDetail]
  })),
  on(fromSearch.setFittingFilter, (state, action) => ({
    ...state,
    activeFittingId: action.fittingProduct?.id,
    activeFittingPartnumber: action.fittingProduct?.partNumber,
    query: {
      hemisphereId: action.fittingProduct?.hemisphereId,
      branchSizeId: action.fittingProduct?.branchSizeId,
      pipeSizeId: action.fittingProduct?.pipeSizeId,
      isDirty: true
    }
  })),
  on(fromActions.changeActiveAssociatedPart, (state, action) => ({
    ...state,
    activeFittingSelectedAssociatedPart: action.partNumber
  })),
  on(fromActions.loadConfigurationLookupOptions, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadConfigurationLookupOptions)
  })),
  on(fromActions.loadConfigurationLookupOptionsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadConfigurationLookupOptions),
    configurationFiltersLookup: action.options
  })),
  on(fromActions.loadConfigurationLookupOptionsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadConfigurationLookupOptions),
  }))
);
